<template>
  <div :class="rootClasses" :style="rootStyle">
    <div
      v-for="i in count"
      :key="`${variant}-${i}`"
      class="d-flex align-center px-2"
      :class="rowClass"
      :style="rowStyle"
    >
      <!-- image-row: 썸네일 + 파일명 (ImageExplorer / ImageListUI) -->
      <template v-if="variant === 'image-row'">
        <v-skeleton-loader
          class="flex-shrink-0 mr-2"
          type="image"
          width="90px"
          height="52px"
        />
        <v-skeleton-loader
          type="text"
          :width="`${40 + ((i * 13) % 35)}%`"
          height="18px"
        />
      </template>

      <!-- text-row: 아이콘 + 한 줄 (모듈리스트 트리) -->
      <template v-else-if="variant === 'text-row'">
        <v-skeleton-loader
          class="flex-shrink-0 mr-2"
          type="avatar"
          width="24px"
          height="24px"
        />
        <v-skeleton-loader
          type="text"
          :width="`${45 + ((i * 17) % 35)}%`"
          height="18px"
        />
      </template>

      <!-- defect-row: 아이콘 + 2줄 (패널플래닝 결함 목록) -->
      <template v-else-if="variant === 'defect-row'">
        <v-skeleton-loader
          class="flex-shrink-0 mr-2"
          type="avatar"
          width="20px"
          height="20px"
        />
        <div class="flex-grow-1" style="min-width: 0">
          <v-skeleton-loader type="text" width="55%" height="14px" class="mb-1" />
          <v-skeleton-loader type="text" width="80%" height="12px" />
        </div>
      </template>

      <!-- tree-row: 프로젝트/사이트 탐색 트리 -->
      <template v-else>
        <v-skeleton-loader
          class="flex-shrink-0 mr-3"
          type="avatar"
          width="28px"
          height="28px"
        />
        <v-skeleton-loader
          type="text"
          :width="`${50 + ((i * 11) % 30)}%`"
          height="20px"
        />
      </template>
    </div>
  </div>
</template>

<script>
/**
 * 좌측 rail 리스트 공통 skeleton — ImageListUI / DefectListDrawer / ImageExplorer 등
 * variant: image-row | text-row | defect-row | tree-row
 */
export default {
  name: 'RailListSkeleton',
  props: {
    variant: {
      type: String,
      default: 'text-row',
      validator: v => ['image-row', 'text-row', 'defect-row', 'tree-row'].includes(v)
    },
    count: {
      type: Number,
      default: 8
    },
    rowHeight: {
      type: Number,
      default: null
    },
    /** 기존 목록 위 반투명 오버레이 (백그라운드 갱신) */
    overlay: {
      type: Boolean,
      default: false
    },
    /** flex-grow-1 등 컨테이너 클래스 */
    rootClass: {
      type: String,
      default: ''
    }
  },
  computed: {
    rootClasses() {
      const base = this.overlay
        ? 'rail-list-skeleton rail-list-skeleton--overlay d-flex flex-column'
        : 'rail-list-skeleton rail-list-skeleton--fill d-flex flex-column flex-grow-1';
      const variantClass =
        this.variant === 'tree-row' ? 'rail-list-skeleton--tree' : '';
      return [base, variantClass, this.rootClass].filter(Boolean).join(' ');
    },
    rootStyle() {
      if (this.overlay) return {};
      return { minHeight: '160px', overflow: 'hidden' };
    },
    rowClass() {
      if (this.variant === 'defect-row') {
        return 'py-2';
      }
      if (this.variant === 'tree-row') {
        return 'rail-list-skeleton__tree-row';
      }
      return '';
    },
    rowStyle() {
      if (this.rowHeight != null) {
        return { height: `${this.rowHeight}px` };
      }
      if (this.variant === 'tree-row') {
        return { minHeight: '48px' };
      }
      return {};
    }
  }
};
</script>

<style scoped>
.rail-list-skeleton--fill {
  background-color: var(--navBgColor);
}

.rail-list-skeleton--tree {
  gap: 8px;
  padding-top: 6px;
  padding-bottom: 6px;
}

.rail-list-skeleton__tree-row {
  padding-top: 10px;
  padding-bottom: 10px;
}

.rail-list-skeleton--overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
  background-color: rgba(128, 128, 128, 0.12);
  overflow: hidden;
  pointer-events: none;
}
</style>
<style>
.v-application.theme--dark .rail-list-skeleton--overlay {
  background-color: rgba(0, 0, 0, 0.35);
}
</style>
